import moment from 'moment-timezone';
import { getDefaultTimeZone } from '@/components/formBuilder/formFields/DateField/helpers';
import { TimeZoneValue, ScheduleSettingsProps } from './types';

export const useScheduleSettings = ({
  timeZone,
  formRef,
  dateFormat,
}: ScheduleSettingsProps) => {
  // To get current time in the specified timezone
  const getCurrentTimeInTimezone = (timezone?: TimeZoneValue): string => {
    const tzKey = timezone?.key || getDefaultTimeZone(timeZone).key;
    const now = moment().tz(tzKey);

    // Round to nearest 15 minutes
    const minutes = Math.round(now.minute() / 15) * 15;
    now.minute(minutes % 60);

    // If rounding pushed us to the next hour
    if (minutes === 60) {
      now.add(1, 'hour').minute(0);
    }

    if (formRef?.current) {
      formRef.current.setValues({
        ...formRef.current.values,
        deliver_time: now.format('HH:mm'),
        timezone: timezone?.key || formRef.current.values.timezone,
      });
    }

    // Format as HH:mm
    return now.format('HH:mm');
  };

  // To get current date in the specified timezone
  const getCurrentDateInTimezone = (timezone?: TimeZoneValue): string => {
    const tzKey = timezone?.key || getDefaultTimeZone(timeZone).key;
    const now = moment().tz(tzKey);
    return now.format('MM/DD/YYYY');
  };

  // To format day of month with ordinal suffix (1st, 2nd, etc.)
  const formatDayWithOrdinal = (day?: number | string): string => {
    if (!day) return '';

    const dayNum = typeof day === 'string' ? parseInt(day, 10) : day;

    if (isNaN(dayNum)) return '';

    const suffixes = ['th', 'st', 'nd', 'rd'];
    const v = dayNum % 100;
    const suffix = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];

    return `${dayNum}${suffix}`;
  };

  // To handle date changes to extract the day
  const handleDateChange = (formattedDateString: string): void => {
    if (formattedDateString && formRef?.current) {
      const date = moment(
        formattedDateString,
        dateFormat?.label || 'MM/DD/YYYY'
      );

      if (date.isValid()) {
        const dayOfMonth = date.date(); // Get the day of the month (1-31)
        formRef.current.setValues({
          ...formRef.current.values,
          deliver_day_of_month: dayOfMonth,
        });
      }
    }
  };

  // To create a date from day of month
  const getDayOfMonthAsDate = (dayOfMonth?: number): Date | null => {
    if (!dayOfMonth || dayOfMonth < 1 || dayOfMonth > 31) return null;

    const currentDate = new Date();
    return new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      dayOfMonth
    );
  };

  return {
    getCurrentTimeInTimezone,
    getCurrentDateInTimezone,
    formatDayWithOrdinal,
    handleDateChange,
    getDayOfMonthAsDate,
  };
};
